import {
  ChevronRight,
  Clock,
  Database,
  Download,
  FileText,
  Filter,
  History,
  Loader2,
  Plus,
  Search,
  Server,
  Settings,
  Shield,
  User,
  Wallet,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AddAuditLogModal } from '../components/audit/AddAuditLogModal';
import { AuditLogDetailModal } from '../components/audit/AuditLogDetailModal';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useToast } from '../components/ui/use-toast';
import { auditApi, type AuditLog } from '../services/auditApi';

const getCategoryIcon = (category: AuditLog['category']) => {
  switch (category) {
    case 'user':
      return <User className='w-4 h-4' />;
    case 'asset':
      return <Database className='w-4 h-4' />;
    case 'compliance':
      return <Shield className='w-4 h-4' />;
    case 'settings':
      return <Settings className='w-4 h-4' />;
    case 'wallet':
      return <Wallet className='w-4 h-4' />;
    case 'report':
      return <FileText className='w-4 h-4' />;
    case 'system':
      return <Server className='w-4 h-4' />;
  }
};

const getSeverityStyles = (severity: AuditLog['severity']) => {
  switch (severity) {
    case 'info':
      return 'bg-info/20 text-info border-info/30';
    case 'warning':
      return 'bg-warning/20 text-warning border-warning/30';
    case 'critical':
      return 'bg-destructive/20 text-destructive border-destructive/30';
  }
};

/** Relative time for timeline (consistent with Wallets page) */
const formatTimeAgo = (date: Date) => {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  if (seconds < 10) return 'Just now';
  if (seconds < 60) return `${seconds} sec ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} hr ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)} day(s) ago`;
  return date.toLocaleDateString();
};

const formatTimeAgoFromTs = (timestamp: number) => formatTimeAgo(new Date(timestamp));

function formatShortAddress(addr: string) {
  if (!addr || addr.length < 12) return addr;
  return `${addr.slice(0, 6)}...${addr.slice(-4)}`;
}

function getTxExplorerUrl(txHash: string) {
  const networkId = parseInt(process.env.REACT_APP_NETWORK_ID || '97', 10);
  return networkId === 97
    ? `https://testnet.bscscan.com/tx/${txHash}`
    : `https://bscscan.com/tx/${txHash}`;
}

const WALLET_TX_STORAGE_KEY = 'vestin-admin-wallet-transactions';
const WALLET_RECEIVES_STORAGE_KEY = 'vestin-admin-wallet-receives';
interface WalletSendEntry {
  txHash: string;
  toAddress: string;
  amount: number;
  timestamp: number;
}
interface WalletReceiveEntry {
  txHash: string;
  fromAddress: string;
  amount: number;
  timestamp: number;
}

function loadWalletSends(): WalletSendEntry[] {
  try {
    const s = localStorage.getItem(WALLET_TX_STORAGE_KEY);
    if (!s) return [];
    const parsed = JSON.parse(s) as WalletSendEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function loadWalletReceives(): WalletReceiveEntry[] {
  try {
    const s = localStorage.getItem(WALLET_RECEIVES_STORAGE_KEY);
    if (!s) return [];
    const parsed = JSON.parse(s) as WalletReceiveEntry[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

const PAGE_SIZE = 20;

type TimelineItem =
  | { type: 'audit'; log: AuditLog; timestamp: number }
  | { type: 'wallet'; tx: WalletSendEntry; timestamp: number }
  | { type: 'wallet-receive'; tx: WalletReceiveEntry; timestamp: number };

export default function AuditLogsPage() {
  const { toast } = useToast();
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [stats, setStats] = useState({ total: 0, today: 0, warning: 0, critical: 0 });
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [severityFilter, setSeverityFilter] = useState<string>('all');
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    page: 1,
    limit: PAGE_SIZE,
  });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [walletSends, setWalletSends] = useState<WalletSendEntry[]>(loadWalletSends);
  const [walletReceives, setWalletReceives] = useState<WalletReceiveEntry[]>(loadWalletReceives);

  const [, setTimeTick] = useState(0);
  const timelineItems = useMemo((): TimelineItem[] => {
    const auditItems: TimelineItem[] = logs.map((log) => ({
      type: 'audit',
      log,
      timestamp:
        log.timestamp instanceof Date ? log.timestamp.getTime() : new Date(log.timestamp).getTime(),
    }));
    const sendItems: TimelineItem[] = walletSends.map((tx) => ({
      type: 'wallet',
      tx,
      timestamp: tx.timestamp,
    }));
    const receiveItems: TimelineItem[] = walletReceives.map((tx) => ({
      type: 'wallet-receive',
      tx,
      timestamp: tx.timestamp,
    }));
    return [...auditItems, ...sendItems, ...receiveItems].sort((a, b) => b.timestamp - a.timestamp);
  }, [logs, walletSends, walletReceives]);

  useEffect(() => {
    if (timelineItems.length === 0) return;
    // Refresh "X min ago" in Activity Timeline every 15s so times stay correct
    const id = setInterval(() => setTimeTick((t) => t + 1), 15000);
    return () => clearInterval(id);
  }, [timelineItems.length]);

  const fetchLogs = useCallback(
    async (pageNum: number = 1, append: boolean = false) => {
      if (append) setLoadingMore(true);
      else setLoading(true);
      try {
        const res = await auditApi.list({
          page: pageNum,
          limit: PAGE_SIZE,
          category: categoryFilter !== 'all' ? categoryFilter : undefined,
          severity: severityFilter !== 'all' ? severityFilter : undefined,
          search: searchQuery.trim() || undefined,
        });
        if (append) {
          setLogs((prev) => (pageNum === 1 ? res.data : [...prev, ...res.data]));
        } else {
          setLogs(res.data);
        }
        setPagination(res.pagination);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load audit logs';
        toast({ title: 'Error', description: message, variant: 'destructive' });
        if (!append) setLogs([]);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [categoryFilter, severityFilter, searchQuery, toast]
  );

  useEffect(() => {
    fetchLogs(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only refetch when category/severity change
  }, [categoryFilter, severityFilter]);

  useEffect(() => {
    const syncWallet = () => {
      setWalletSends(loadWalletSends());
      setWalletReceives(loadWalletReceives());
    };
    syncWallet();
    const onFocus = () => syncWallet();
    const onVisible = () => {
      if (document.visibilityState === 'visible') syncWallet();
    };
    const onReceivesUpdated = () => syncWallet();
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('vestin-wallet-receives-updated', onReceivesUpdated);
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') syncWallet();
    }, 15000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('vestin-wallet-receives-updated', onReceivesUpdated);
      clearInterval(interval);
    };
  }, []);

  const applySearch = useCallback(() => {
    fetchLogs(1, false);
  }, [fetchLogs]);

  const fetchStats = useCallback(async () => {
    try {
      const s = await auditApi.getStats();
      setStats(s);
    } catch {
      setStats({ total: 0, today: 0, warning: 0, critical: 0 });
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleSearchSubmit = () => {
    applySearch();
  };

  const handleLoadMore = () => {
    if (pagination.page < pagination.totalPages && !loadingMore) {
      fetchLogs(pagination.page + 1, true);
    }
  };

  const handleLogClick = (log: AuditLog) => {
    setSelectedLog(log);
    setDetailModalOpen(true);
  };

  const handleLogAdded = (log: AuditLog) => {
    setLogs((prev) => [log, ...prev]);
    fetchStats();
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const rows = await auditApi.listForExport({
        limit: 5000,
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        severity: severityFilter !== 'all' ? severityFilter : undefined,
        search: searchQuery.trim() || undefined,
      });
      const headers = ['Action', 'Category', 'Actor', 'Details', 'IP', 'Severity', 'Timestamp'];
      const csvRows = [
        headers.join(','),
        ...rows.map((l) =>
          [
            `"${(l.action ?? '').replace(/"/g, '""')}"`,
            l.category,
            `"${(l.actor ?? '').replace(/"/g, '""')}"`,
            `"${(l.details ?? '').replace(/"/g, '""')}"`,
            l.ipAddress ?? '',
            l.severity,
            l.timestamp instanceof Date ? l.timestamp.toISOString() : l.timestamp,
          ].join(',')
        ),
      ];
      const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast({ title: 'Exported', description: `${rows.length} log(s) exported as CSV.` });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      toast({ title: 'Export failed', description: message, variant: 'destructive' });
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className='space-y-6 animate-fade-in'>
      {/* Header */}
      <div className='flex flex-col lg:flex-row lg:items-center justify-between gap-4'>
        <div className='flex items-center gap-4'>
          <div className='w-12 h-12 rounded-xl bg-gradient-to-br from-secondary to-secondary/50 flex items-center justify-center'>
            <History className='w-6 h-6 text-secondary-foreground' />
          </div>
          <div>
            <h1 className='text-2xl font-bold tracking-tight'>Audit Logs</h1>
            <p className='text-muted-foreground'>
              Track all administrative actions and system events
            </p>
          </div>
        </div>
        <div className='flex gap-2'>
          <Button variant='outline' onClick={handleExport} disabled={exporting}>
            {exporting ? (
              <Loader2 className='w-4 h-4 mr-2 animate-spin' />
            ) : (
              <Download className='w-4 h-4 mr-2' />
            )}
            Export Logs
          </Button>
          <Button onClick={() => setAddModalOpen(true)}>
            <Plus className='w-4 h-4 mr-2' />
            Add Entry
          </Button>
        </div>
      </div>

      <AuditLogDetailModal
        log={selectedLog}
        open={detailModalOpen}
        onOpenChange={setDetailModalOpen}
      />

      <AddAuditLogModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        onLogAdded={handleLogAdded}
      />

      {/* Stats */}
      <div className='grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4'>
        <Card className='gradient-card border-border/30'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-lg bg-primary/20 flex items-center justify-center'>
                <History className='w-5 h-5 text-primary' />
              </div>
              <div>
                <p className='text-2xl font-bold'>{stats.total}</p>
                <p className='text-xs text-muted-foreground'>Total Events</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className='gradient-card border-border/30'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-lg bg-info/20 flex items-center justify-center'>
                <Clock className='w-5 h-5 text-info' />
              </div>
              <div>
                <p className='text-2xl font-bold'>{stats.today}</p>
                <p className='text-xs text-muted-foreground'>Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className='gradient-card border-border/30'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-lg bg-warning/20 flex items-center justify-center'>
                <Shield className='w-5 h-5 text-warning' />
              </div>
              <div>
                <p className='text-2xl font-bold'>{stats.warning}</p>
                <p className='text-xs text-muted-foreground'>Warnings</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className='gradient-card border-border/30'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-3'>
              <div className='w-10 h-10 rounded-lg bg-destructive/20 flex items-center justify-center'>
                <Shield className='w-5 h-5 text-destructive' />
              </div>
              <div>
                <p className='text-2xl font-bold'>{stats.critical}</p>
                <p className='text-xs text-muted-foreground'>Critical</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className='gradient-card border-border/30'>
        <CardContent className='p-3 sm:p-4'>
          <div className='flex flex-col sm:flex-row gap-3 sm:gap-4'>
            <div className='relative flex-1'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
              <Input
                placeholder='Search logs...'
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchSubmit()}
                className='pl-10 bg-muted/50 border-border/50 text-sm'
              />
            </div>
            <Button type='button' variant='secondary' onClick={handleSearchSubmit}>
              Search
            </Button>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className='w-full sm:w-[160px] lg:w-[180px] bg-muted/50 border-border/50'>
                <Filter className='w-4 h-4 mr-2' />
                <SelectValue placeholder='Category' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Categories</SelectItem>
                <SelectItem value='user'>User</SelectItem>
                <SelectItem value='asset'>Asset</SelectItem>
                <SelectItem value='compliance'>Compliance</SelectItem>
                <SelectItem value='settings'>Settings</SelectItem>
                <SelectItem value='wallet'>Wallet</SelectItem>
                <SelectItem value='report'>Report</SelectItem>
                <SelectItem value='system'>System</SelectItem>
              </SelectContent>
            </Select>
            <Select value={severityFilter} onValueChange={setSeverityFilter}>
              <SelectTrigger className='w-full sm:w-[140px] lg:w-[150px] bg-muted/50 border-border/50'>
                <SelectValue placeholder='Severity' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Severity</SelectItem>
                <SelectItem value='info'>Info</SelectItem>
                <SelectItem value='warning'>Warning</SelectItem>
                <SelectItem value='critical'>Critical</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Logs List */}
      <Card className='gradient-card border-border/30'>
        <CardHeader className='pb-2'>
          <CardTitle className='text-lg'>Activity Timeline</CardTitle>
        </CardHeader>
        <CardContent className='p-0'>
          <ScrollArea className='h-[500px]'>
            {loading ? (
              <div className='flex items-center justify-center py-12'>
                <Loader2 className='w-8 h-8 animate-spin text-muted-foreground' />
              </div>
            ) : (
              <div className='divide-y divide-border/50'>
                {timelineItems.map((item) =>
                  item.type === 'audit' ? (
                    <div
                      key={`audit-${item.log.id}`}
                      onClick={() => handleLogClick(item.log)}
                      className='flex items-start gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-muted/30 transition-colors cursor-pointer group'
                    >
                      <div
                        className={`w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0 ${getSeverityStyles(item.log.severity)}`}
                      >
                        {getCategoryIcon(item.log.category)}
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2 flex-wrap'>
                          <h4 className='font-medium group-hover:text-primary transition-colors text-sm sm:text-base'>
                            {item.log.action}
                          </h4>
                          <Badge variant='outline' className='text-xs capitalize'>
                            {item.log.category}
                          </Badge>
                        </div>
                        <p className='text-sm text-muted-foreground mt-1 line-clamp-2'>
                          {item.log.details}
                        </p>
                        <div className='flex items-center gap-2 sm:gap-4 mt-2 text-xs text-muted-foreground flex-wrap'>
                          <span className='truncate max-w-[120px] sm:max-w-none'>
                            {item.log.actor}
                          </span>
                          <span className='hidden sm:inline'>•</span>
                          <span className='truncate'>{item.log.ipAddress}</span>
                        </div>
                      </div>
                      <div className='text-right shrink-0 hidden sm:block'>
                        <p className='text-sm text-muted-foreground'>
                          {formatTimeAgoFromTs(item.timestamp)}
                        </p>
                        <ChevronRight className='w-4 h-4 text-muted-foreground mt-2 ml-auto opacity-0 group-hover:opacity-100 transition-opacity' />
                      </div>
                      <div className='text-left sm:text-right shrink-0 sm:hidden'>
                        <p className='text-xs text-muted-foreground'>
                          {formatTimeAgoFromTs(item.timestamp)}
                        </p>
                      </div>
                    </div>
                  ) : item.type === 'wallet-receive' ? (
                    <div
                      key={`receive-${item.tx.txHash}`}
                      className='flex items-start gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-muted/30 transition-colors group'
                    >
                      <div className='w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0 bg-success/20 text-success border border-success/30'>
                        <Wallet className='w-4 h-4 sm:w-5 sm:h-5' />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2 flex-wrap'>
                          <h4 className='font-medium text-sm sm:text-base'>Token received</h4>
                          <Badge variant='outline' className='text-xs'>
                            wallet
                          </Badge>
                        </div>
                        <p className='text-sm text-muted-foreground mt-1'>
                          Received{' '}
                          {Number(item.tx.amount).toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })}{' '}
                          from {formatShortAddress(item.tx.fromAddress)}
                        </p>
                      </div>
                      <div className='text-right shrink-0 flex items-center gap-2'>
                        <p className='text-sm text-muted-foreground hidden sm:block'>
                          {formatTimeAgoFromTs(item.timestamp)}
                        </p>
                        <a
                          href={getTxExplorerUrl(item.tx.txHash)}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-primary hover:underline text-xs sm:text-sm'
                          onClick={(e) => e.stopPropagation()}
                        >
                          View tx
                        </a>
                        <ChevronRight className='w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity' />
                      </div>
                      <div className='text-left sm:hidden shrink-0'>
                        <p className='text-xs text-muted-foreground'>
                          {formatTimeAgoFromTs(item.timestamp)}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div
                      key={`wallet-${item.tx.txHash}`}
                      className='flex items-start gap-3 sm:gap-4 p-3 sm:p-4 hover:bg-muted/30 transition-colors group'
                    >
                      <div className='w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center shrink-0 bg-primary/20 text-primary border border-primary/30'>
                        <Wallet className='w-4 h-4 sm:w-5 sm:h-5' />
                      </div>
                      <div className='flex-1 min-w-0'>
                        <div className='flex items-center gap-2 flex-wrap'>
                          <h4 className='font-medium text-sm sm:text-base'>Token transfer</h4>
                          <Badge variant='outline' className='text-xs'>
                            wallet
                          </Badge>
                        </div>
                        <p className='text-sm text-muted-foreground mt-1'>
                          Sent{' '}
                          {Number(item.tx.amount).toLocaleString(undefined, {
                            maximumFractionDigits: 2,
                          })}{' '}
                          to {formatShortAddress(item.tx.toAddress)}
                        </p>
                      </div>
                      <div className='text-right shrink-0 flex items-center gap-2'>
                        <p className='text-sm text-muted-foreground hidden sm:block'>
                          {formatTimeAgoFromTs(item.timestamp)}
                        </p>
                        <a
                          href={getTxExplorerUrl(item.tx.txHash)}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='text-primary hover:underline text-xs sm:text-sm'
                          onClick={(e) => e.stopPropagation()}
                        >
                          View tx
                        </a>
                        <ChevronRight className='w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity' />
                      </div>
                      <div className='text-left sm:hidden shrink-0'>
                        <p className='text-xs text-muted-foreground'>
                          {formatTimeAgoFromTs(item.timestamp)}
                        </p>
                      </div>
                    </div>
                  )
                )}
              </div>
            )}
            {!loading && pagination.totalPages > 1 && pagination.page < pagination.totalPages && (
              <div className='p-4 flex justify-center'>
                <Button variant='outline' onClick={handleLoadMore} disabled={loadingMore}>
                  {loadingMore ? <Loader2 className='w-4 h-4 animate-spin mr-2' /> : null}
                  Load more
                </Button>
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
}
